import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { beforeActionBarDockMoves, useActionBarDock } from './StickyActionBar';
import { queueTelemetry, type TelemetryOutcome } from './telemetry';

// A small, unobtrusive "Report a problem" affordance (Phase 4, House O + H —
// give the people who use these tools a one-click way to tell us when something
// is wrong). Sends a user_report telemetry event to the same store as automatic
// captures, so user-felt issues sit alongside the errors in the doctor digest.
//
// Colours read the semantic ROLE tokens only (the tokens.css contract), so the
// pill and the panel follow the active theme, night included (cca-3l485). The
// fallbacks are the light portal values, for a host that loads no theme. The
// muted grey and the UI face have no fallback: tokens.css declares both, every
// host loads that file, and a second spelling of the grey here is how the old
// grey outlived its fix.
//
// Mount it AFTER the page content (each portal's main.tsx does): on phones,
// unless a sticky action bar docks it (below), it also leaves an empty in-flow
// spacer at the foot of the page, sized in components.css, so the page's last
// line can scroll clear of the launcher.
//
// IN a sticky action bar (bead cca-00fff; WCAG 2.2 SC 2.4.11): on a phone a
// form's StickyActionBar pins its full-width row to the screen's bottom edge,
// right where this launcher sat (cca-rnghf measured a 5,054 px² overlap on
// forms-web's /p/<key>). cca-rnghf stood the launcher 16px above the bar — and
// that is exactly where the focus reservation (components.css, cca-pftjr) parks
// each Tab-reached field, so a right-hand field was up to 88% covered at 390×900
// and DateField's calendar button wholly hidden at 320×256, 400% zoom (Miranda
// r1 on #641). So on a phone only one layer owns the bottom edge: while a bar is
// sticky it offers a dock — its empty last child, pushed to the trailing edge —
// and this component renders its launcher and panel INTO it through a portal
// (useActionBarDock), in flow: no fixed box, no offsets. The bar's own controls
// are exempt from the reservation, so the docked launcher covers nothing. There
// it wears the shared ghost button, which components.css quiets to the muted
// grey: a 44px control that does not compete with the bar's own action. The
// panel still opens upward, positioned on the bar itself, so it stands above
// the bar's top edge and never over the bar's own controls; and no spacer is
// rendered, because the bar is in flow and the page's last line already scrolls
// clear of it. With no dock — a desktop row, a page with no bar, a bare
// `.action-bar`, a server render — it is the fixed corner it was, and that
// corner still adds any published bar height (`--cca-action-bar-height`, 0px
// when none) to its `bottom`, its panel's cap and its phone spacer.
//
// Moving between the two (the bar mounts or leaves, a width change across the
// breakpoint) builds the launcher and panel afresh in their new place. Whether
// the panel is open, the words typed and what the status region says are this
// component's state, so they come along. Focus would not survive the old nodes,
// so it is carried (Miranda, cca-00fff): at the moment the dock changes hands,
// before the old nodes go, the component notes which of its OWN controls has
// focus — the launcher, the textarea with its caret, Cancel or Send — and once
// the new nodes mount it puts focus back on the same control, caret and all,
// without scrolling. Focus anywhere else on the page — a form field, the page
// itself — is not noted and is left exactly where it is: the panel never takes
// focus from the form. The textarea takes focus on its own only when the person
// opens the panel (no autoFocus, which used to pull focus into a panel left open
// whenever its nodes were rebuilt).
//
// Opening while docked at the page's foot: the panel's height cap is measured
// from a bar pinned to the screen's foot, but at the foot of the page the bar
// sits in flow above the footer, higher up the screen, so a tall panel opened
// with its top above the screen's top edge (Tyson, 320×256: 30.9px on the claim
// form, 94.6px on the anonymous form). On opening, the component measures the
// panel's top and, only when it is above the screen, scrolls the page back by
// exactly that much, instantly — never smoothly, whatever the host's
// scroll-behavior — so the whole panel is on screen as it is first painted. A
// browser that does not know 'instant' gets the same distance through the plain
// two-number scroll instead of an error (scrollBackInstantly). It neither
// shrinks the cap nor opens downward. Focusing the textarea then moves
// nothing: focus() alone scrolled the page by up to 391px on opening a panel
// already wholly in view at 320×256 (Tyson, cca-00fff).
//
// Keyboard and assistive tech (cca-4hiyz; WCAG 2.2 SC 2.4.3 + 4.1.3, UX-3/4/6):
// the DISCLOSURE pattern. The launcher stays on screen as the toggle, carrying
// aria-expanded and aria-controls naming the panel. Cancel, Escape (from the
// panel or the launcher) and the launcher itself close it and put focus back on
// the launcher; the panel's Escape goes no further, so one press does not also
// close a drawer listening on the document. An accepted Send puts focus there at
// once, because Send leaves with the form; the thanks then closes itself without
// moving focus again. The heading is the textarea's <label>. What happened is
// said in a status region that is ALWAYS in the DOM, so the words that appear in
// it are read out: "sent" only when queueTelemetry sent the report, and otherwise
// the gate that stopped it, in the person's terms, with UX-6's way to reach the
// registrar. Every refused press empties the region and fills it again a tick
// later, so pressing Send on refused words a second time is said again, not met
// with silence. The thanks and a refusal wear the shared .alert-success and
// .alert-warn, so the state shows in form as well as words; the label, textarea
// and buttons wear the shared .label / .textarea / .btn, which follow the theme
// and carry MOB-2's phone floors (16px text, 44px targets).
//
// A host may add ONE line of its own (`notice`, cca-dorka): something true of the
// page the panel is opened from that the person should know before they type —
// forms-web's anonymous survey says the message is kept apart from the answers.
// The words are the host's; this panel carries none of its own. The line sits
// under the label in the shared muted .help, and it is the textarea's
// description (aria-describedby), so it is read out with the label as focus
// lands in the box. Without it the panel is exactly as it was.

/** UX-6's escape hatch names this address (docs/standards/ux.md). */
const REGISTRAR_EMAIL = 'registrar@confessinganglicans.nz';

/** How long the thanks shows before the panel closes itself. */
const CLOSE_AFTER_MS = 1800;

/** Every answer but 'sent' is a refusal. */
type Refusal = Exclude<TelemetryOutcome, 'sent'>;

/** What each refusal says: the gate that stopped the report, in the person's terms. */
const REFUSED: Record<Refusal, string> = {
  repeat: "You've already sent this report from this page.",
  cap: "This page can't send any more reports just now.",
  failed: 'This report could not be sent.',
  // Unreachable here (Send is disabled until there are words), but named, so no
  // answer the reporter can give goes unsaid.
  empty: 'This report could not be sent.',
};

/** How much of the screen's height the open panel leaves free, in px — four
 *  figures, top to bottom of the corner it sits in:
 *    16  the fixed box's offset above the bottom edge (its `bottom`, below);
 *    44  the launcher's tallest footprint: MOB-2's 44px phone floor
 *        (components.css `.report-problem-launcher`; the desktop pill is
 *        shorter, about 34px, so 44 covers both);
 *     8  the panel's own gap above the launcher (PANEL `marginBottom`);
 *     8  air above the panel's top edge, as `.datefield-pop` keeps.
 *  PLUS a sticky action bar's height, when the page has one (below).
 *  report-problem.test.ts reads the first three back from this file and the
 *  stylesheet, so a launcher that grows makes the sum too small and goes red. */
const PANEL_CLEARANCE = 16 + 44 + 8 + 8;

/** The open panel's height cap in the corner: the screen less the corner above,
 *  less any sticky action bar the box stands on (`--cca-action-bar-height`,
 *  published by StickyActionBar; 0px when the page has none). */
const PANEL_MAX_HEIGHT = `calc(100dvh - ${PANEL_CLEARANCE}px - var(--cca-action-bar-height, 0px))`;

/** Docked in a sticky action bar (cca-00fff), the panel stands on the bar's own
 *  top edge, so two of those figures remain: its 8px gap above the bar (PANEL
 *  `marginBottom`) and the 8px of air above its top. The bar's height is taken
 *  off as before, and it is the docking bar's own: the dock is the bar whose
 *  height is published (StickyActionBar's chooseDock). */
const DOCKED_CLEARANCE = 8 + 8;

/** The open panel's height cap while docked: the screen less the bar, the gap
 *  and the air. Measured from the bar where it is pinned, at the screen's foot;
 *  where the bar sits higher (in flow at the page's foot), opening scrolls the
 *  panel's top back onto the screen instead (the component's open path). */
const DOCKED_MAX_HEIGHT = `calc(100dvh - ${DOCKED_CLEARANCE}px - var(--cca-action-bar-height, 0px))`;

/** The open panel. Closed, the same element stays in the DOM, undrawn.
 *  Border-box, so its 280px is its whole width, border and padding included: at
 *  320 CSS px (SC 1.4.10's reflow width) it sits 24px clear of the left edge.
 *  Never taller than the screen allows (SC 1.4.10, Miranda r3 on cca-0m45): it
 *  grows UP from the launcher, so with no cap a short screen — 400% zoom, about
 *  256 CSS px tall, or a landscape phone — pushed its top, the label and any
 *  notice, past the top edge where nothing could scroll to them. Capped, it
 *  scrolls inside its own box, sideways never: the estate's `.datefield-pop`
 *  pattern (components.css). */
const PANEL: CSSProperties = {
  boxSizing: 'border-box',
  width: 280,
  marginBottom: 8,
  background: 'var(--color-panel, #fff)',
  color: 'var(--color-ink, #111827)',
  border: '1px solid var(--color-border, #e5e0d5)',
  borderRadius: 10,
  boxShadow: '0 6px 24px rgba(0,0,0,.18)',
  padding: 12,
  fontFamily: 'var(--font-ui)',
  fontSize: 13,
  maxHeight: PANEL_MAX_HEIGHT,
  overflow: 'hidden auto',
};

/** The open panel docked in a sticky action bar (cca-00fff): the same panel,
 *  positioned on the BAR — sticky, so it is the containing block; the dock
 *  itself is unpositioned (components.css) — rather than on the screen. Its
 *  foot stands its 8px gap above the bar's top edge and its right edge on the
 *  bar's, which is the launcher's trailing edge (the bar has no inline padding
 *  and the dock is pushed to the end of its row). Anchored on the dock instead,
 *  a bar whose launcher had wrapped onto a row of its own would have the panel
 *  open over the bar's first row — its primary action, the very control that
 *  Shift+Tab out of the panel lands on. */
const DOCKED_PANEL: CSSProperties = {
  ...PANEL,
  position: 'absolute',
  right: 0,
  bottom: '100%',
  maxHeight: DOCKED_MAX_HEIGHT,
};

/** The fixed corner the launcher and its panel sit in when no bar docks them. */
const CORNER: CSSProperties = {
  position: 'fixed',
  right: 16,
  // 16px above the bottom edge, or above a sticky action bar (cca-rnghf).
  bottom: 'calc(16px + var(--cca-action-bar-height, 0px))',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
};

/** Docked, the box is in flow: no position, no offsets, only a row that keeps
 *  the launcher clear of a line box's descender space. */
const DOCKED_BOX: CSSProperties = { display: 'flex' };

/** The launcher in the corner: a small pill on the panel colour, floating over
 *  the page. Docked, it wears DOCKED_LAUNCHER and no style of its own. */
const PILL: CSSProperties = {
  background: 'var(--color-panel, #fff)',
  color: 'var(--color-muted)',
  border: '1px solid var(--color-border, #e5e0d5)',
  borderRadius: 999,
  boxShadow: '0 2px 8px rgba(0,0,0,.12)',
  padding: '7px 12px',
  fontSize: 12,
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer',
};

/** The docked launcher's classes: the shared ghost button, small — its face,
 *  focus ring and MOB-2 phone floor come with .btn — which components.css
 *  quiets to the muted grey inside `.action-bar-dock`. */
const DOCKED_LAUNCHER = 'report-problem-launcher btn btn-ghost btn-sm';

/** One of the launcher's own controls, as noted when the dock changes hands (the
 *  textarea with its caret), so focus can be put back on it in its new place. */
type Held =
  { control: 'launcher' | 'cancel' | 'send' } | { control: 'textarea'; start: number; end: number };

/** Scroll the page by `top` px (negative: back up), instantly. `behavior` is a
 *  WebIDL enum, so a browser that does not know 'instant' throws a TypeError
 *  rather than ignoring it — and this runs in a layout effect, where a throw
 *  reaches the app-wide TelemetryErrorBoundary (vetting, Finance) and replaces a
 *  half-filled form with the fallback (Miranda S2, Lazo, cca-00fff). So on any
 *  throw it scrolls by the two-number form, which takes no behavior and follows
 *  the page's scroll-behavior: the same distance, and never an error. */
function scrollBackInstantly(top: number): void {
  try {
    window.scrollBy({ top, behavior: 'instant' });
  } catch {
    window.scrollBy(0, top);
  }
}

/** The status region while it says something (it wears .alert then). The address
 *  may wrap rather than spill out of the box on a narrow phone. */
const SAID: CSSProperties = { overflowWrap: 'anywhere' };

const ACTIONS: CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  marginTop: 8,
};

/** What a host may give the launcher. Optional: a host that gives nothing gets
 *  the panel exactly as it was. */
export interface ReportProblemProps {
  /** One short line of the HOST's words, shown in the open panel under its label
   *  and given to the textarea as its description. Absent or empty: no line and
   *  no description. */
  notice?: string | undefined;
}

export function ReportProblem({ notice }: ReportProblemProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  // What the status region says: the last Send's answer, or nothing.
  const [outcome, setOutcome] = useState<TelemetryOutcome | null>(null);
  // A refusal waiting a tick to be said, after the region has been emptied.
  const [pending, setPending] = useState<Refusal | null>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const sendButton = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const boxId = useId();
  const noticeId = useId();
  // A sticky action bar's dock, or null: the fixed corner (cca-00fff).
  const dock = useActionBarDock();
  // Set by the person's press on the launcher, read once as the open panel
  // mounts: only that press scrolls the panel on screen and focuses the textarea.
  const opening = useRef(false);
  // Which of this component's controls had focus as the dock changed hands.
  const held = useRef<Held | null>(null);

  // Noted at the hand-off itself, while the old nodes are still on the page —
  // not when the bar's ResizeObserver fired, a frame earlier (Miranda, cca-00fff).
  // Focus elsewhere on the page — a form field, the page itself — is not ours and
  // notes nothing. A note stands until the new nodes mount: when one bar leaves
  // and another arrives in the same commit, the second hand-off comes after the
  // first bar's nodes are gone, and must not wipe what the first one saw.
  useLayoutEffect(
    () =>
      beforeActionBarDockMoves(() => {
        if (held.current !== null) return;
        const active = document.activeElement;
        const field = textarea.current;
        if (active === null) return;
        if (active === launcher.current) held.current = { control: 'launcher' };
        else if (active === field && field !== null) {
          held.current = {
            control: 'textarea',
            start: field.selectionStart,
            end: field.selectionEnd,
          };
        } else if (active === cancelButton.current) held.current = { control: 'cancel' };
        else if (active === sendButton.current) held.current = { control: 'send' };
      }),
    [],
  );

  // The new nodes are in: focus goes back on the control that had it, caret and
  // all, without scrolling — but only if it fell to the page with the old nodes.
  // Wherever else it is now, it stays.
  useLayoutEffect(() => {
    const was = held.current;
    held.current = null;
    if (was === null) return;
    const now = document.activeElement;
    if (now !== null && now !== document.body) return;
    if (was.control === 'textarea') {
      textarea.current?.focus({ preventScroll: true });
      textarea.current?.setSelectionRange(was.start, was.end);
      return;
    }
    const back = { launcher, cancel: cancelButton, send: sendButton }[was.control];
    back.current?.focus({ preventScroll: true });
  }, [dock]);

  // Opened by the person: docked at the page's foot, the panel's top can start
  // above the screen (the bar sits in flow above the footer there, higher than
  // the pinned bar its cap is measured from), so the page comes down by exactly
  // that shortfall — instantly, never smoothly, whatever the host's
  // scroll-behavior (where the browser knows 'instant'; see scrollBackInstantly)
  // — before the frame is painted. A panel already wholly on screen moves
  // nothing, and focusing the textarea then moves nothing either.
  useLayoutEffect(() => {
    if (!open || !opening.current) return;
    opening.current = false;
    const top = dock ? (panel.current?.getBoundingClientRect().top ?? 0) : 0;
    if (top < 0) scrollBackInstantly(top);
    textarea.current?.focus({ preventScroll: true });
  }, [open]);

  const close = (): void => {
    setOpen(false);
    setOutcome(null);
    setPending(null);
  };
  // Closed from inside — Cancel, Escape, the launcher: focus goes back to the
  // launcher rather than falling to the page (UX-3).
  const closeToLauncher = (): void => {
    close();
    launcher.current?.focus();
  };

  // The thanks shows briefly, then the panel closes. Focus is already on the
  // launcher (submit put it there), so this leaves it wherever the person now is.
  useEffect(() => {
    if (outcome !== 'sent') return;
    const timer = setTimeout(close, CLOSE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [outcome]);

  // A refusal is said a tick after the region was emptied, so the same refusal
  // twice is still a change a screen reader reads out (SC 4.1.3). Closing the
  // panel or editing the words drops a refusal still waiting.
  useEffect(() => {
    if (pending === null) return;
    const timer = setTimeout(() => {
      setOutcome(pending);
      setPending(null);
    }, 0);
    return () => clearTimeout(timer);
  }, [pending]);

  // The Send button is the only caller, and it is disabled until the trimmed text is
  // non-empty (same render, same text), so an empty report never reaches here.
  const submit = (): void => {
    const answer = queueTelemetry('user_report', text.trim());
    if (answer === 'sent') {
      setPending(null);
      setText('');
      setOutcome('sent');
      // Send leaves with the form: focus goes to the launcher now, not to the page.
      launcher.current?.focus();
    } else {
      // Refused. The words stay, so they can be copied into an email, and Send
      // stays where it is, enabled: the region is emptied now and says why on the
      // next tick.
      setOutcome(null);
      setPending(answer);
    }
  };

  const refusal: Refusal | null = outcome === null || outcome === 'sent' ? null : outcome;

  const box = (
    // role="presentation": this box is no control. It only catches Escape as the
    // key bubbles up from the panel's controls or the launcher — jsx-a11y's
    // documented signal for a container that catches bubbled events. Docked, the
    // key still bubbles here first: a portal's events follow the React tree.
    <div
      role="presentation"
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !open) return;
        // The open panel's Escape: it goes no further, so one press does not
        // also close a SettingsDrawer (it listens on the document).
        e.stopPropagation();
        e.preventDefault();
        closeToLauncher();
      }}
      style={dock ? DOCKED_BOX : CORNER}
    >
      <div ref={panel} id={panelId} style={open ? (dock ? DOCKED_PANEL : PANEL) : undefined}>
        {open && outcome !== 'sent' && (
          <>
            <div className="field">
              <label htmlFor={boxId} className="label">
                Report a problem
              </label>
              {/* The host's line, when it gives one. The shared .help sets the
                  muted grey and the small size, so its colour is the theme's;
                  the margin is zeroed because the .field grid spaces it. */}
              {notice ? (
                <p id={noticeId} className="help" style={{ margin: 0 }}>
                  {notice}
                </p>
              ) : null}
              <textarea
                ref={textarea}
                id={boxId}
                className="textarea"
                aria-describedby={notice ? noticeId : undefined}
                // No autoFocus: the person's open press focuses it (the open
                // path above), and a rebuild across a dock change puts focus
                // back only where it was.
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setOutcome(null);
                  setPending(null);
                }}
                placeholder="What were you doing, and what went wrong?"
                rows={4}
                // Three of the four portals load no base.css, so no border-box reset
                // reaches here: .textarea's 100% width plus its padding would spill
                // out of the panel. Nothing else inline — a size here would beat the
                // class and its 16px phone floor.
                style={{ boxSizing: 'border-box' }}
              />
            </div>
            <div style={ACTIONS}>
              <button
                ref={cancelButton}
                type="button"
                className="btn btn-ghost"
                onClick={closeToLauncher}
              >
                Cancel
              </button>
              <button
                ref={sendButton}
                type="button"
                className="btn btn-primary"
                onClick={submit}
                disabled={!text.trim()}
              >
                Send
              </button>
            </div>
          </>
        )}
        {/* Always in the DOM, open or closed, so the words that appear in it are
            read out (WCAG 2.2 SC 4.1.3). */}
        <div
          role="status"
          className={
            outcome === null
              ? undefined
              : outcome === 'sent'
                ? 'alert alert-success'
                : 'alert alert-warn'
          }
          style={outcome === null ? undefined : { ...SAID, marginTop: refusal ? 8 : 0 }}
        >
          {outcome === 'sent' && 'Sent to the registrar. Thank you.'}
          {refusal && (
            <>
              {REFUSED[refusal]}
              <br />
              Having any problems? Email{' '}
              <a className="btn-link" href={`mailto:${REGISTRAR_EMAIL}`}>
                {REGISTRAR_EMAIL}
              </a>
            </>
          )}
        </div>
      </div>
      <button
        ref={launcher}
        type="button"
        className={dock ? DOCKED_LAUNCHER : 'report-problem-launcher'}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={
          open
            ? closeToLauncher
            : () => {
                opening.current = true;
                setOpen(true);
              }
        }
        title="Report a problem to the registrar"
        style={dock ? undefined : PILL}
      >
        Report a problem
      </button>
    </div>
  );

  // Docked: the launcher and panel render inside the bar's dock, and no spacer
  // (the bar is in flow, so the page's last line already scrolls clear of it).
  if (dock) return createPortal(box, dock);

  return (
    <>
      {/* Empty and unseen; shown on phones only (components.css). Rendered open or
          closed, so opening the panel never changes the page's length. */}
      <div className="report-problem-spacer" aria-hidden="true" />
      {box}
    </>
  );
}
